<template>
  <div class="page-content">
    <div class="row">
      <div class="col-md-12 grid-margin stretch-card">
        <div class="card">
          <div class="card-header">
            <div class="row align-items-center">
              <div class="col-8">
                <h4 class="mb-1">Responses</h4>
                <p class="mb-0 text-muted">{{ blog.header || '-' }}</p>
                <p class="mb-0 text-muted" v-if="blog.cta_question">
                  <small>{{ blog.cta_question }}</small>
                </p>
              </div>
              <div class="col-4 text-right">
                <b-button variant="outline-secondary" class="mr-2" @click="$router.back()">Back</b-button>
                <b-button variant="primary" :disabled="loading.export" @click="exportCsv">
                  {{ loading.export ? 'Preparing...' : 'Export CSV' }}
                </b-button>
              </div>
            </div>
          </div>
          <div class="card-body">
            <div class="row mb-4">
              <div class="col-md-4 mb-3" v-for="card in summaryCards" :key="card.key">
                <div class="border rounded p-3">
                  <p class="mb-1 text-muted">{{ card.label }}</p>
                  <h3 class="mb-0" :class="card.class">{{ card.total }}</h3>
                </div>
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-4">
                <el-select class="w-100" v-model="search.response" @change="doSearch" placeholder="All responses" clearable>
                  <el-option
                    v-for="item in responseOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value">
                  </el-option>
                </el-select>
              </div>
            </div>

            <div class="table-responsive">
              <table class="table table-hover" v-loading="loading.table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Company</th>
                    <th>Workspace</th>
                    <th>Response</th>
                    <th>Responded At</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, i) in rows" :key="i">
                    <td>{{ item.user_name || '-' }}</td>
                    <td>{{ item.user_email || '-' }}</td>
                    <td>{{ item.company_name || '-' }}</td>
                    <td>{{ item.workspace_name || '-' }}</td>
                    <td>
                      <span class="badge pb-2" :class="badgeClass(item.response)">
                        {{ labelFor(item.response) }}
                      </span>
                    </td>
                    <td>{{ item.responded_at ? moment(item.responded_at).format('DD MMM YYYY HH:mm') : '-' }}</td>
                  </tr>
                </tbody>
              </table>
              <el-empty v-if="!rows.length && !loading.table" description="No responses yet"></el-empty>
            </div>
            <b-pagination
              v-if="total_row > search.per_page"
              v-model="currentPage"
              :total-rows="total_row"
              :per-page="search.per_page"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import moment from 'moment';
import blogNotifApi from '../../api/blogNotif';
import popupErrorMessages from '../../library/popup-error-messages';

/**
 * WAP-407 — rekap jawaban satu announcement, RSVP maupun poll.
 *
 * Angka di kartu ringkasan datang dari `summary` milik BE (dihitung dari seluruh koleksi),
 * bukan dari baris yang sedang tampil, supaya tidak berubah saat ganti halaman atau filter.
 *
 * Label pilihan poll di-resolve DI SINI dari `cta_options` milik announcement-nya: halaman
 * ini memang sudah mengambil detail announcement untuk judulnya, jadi BE tidak perlu query
 * tambahan hanya untuk mengirim teks yang sudah ada di tangan kita.
 */
export default {
  name: 'Notification Blogs Responses',
  metaInfo: {
    title: 'Responses',
  },
  data() {
    return {
      moment,
      loading: {
        table: false,
        export: false,
      },
      blog: {},
      rows: [],
      total_row: 0,
      summary: {
        interested: 0,
        not_interested: 0,
        total: 0,
        by_response: {},
      },
      search: {
        response: '',
        page: 1,
        per_page: 10,
      },
    };
  },
  computed: {
    isPoll() {
      return this.blog.cta_type === 'poll';
    },
    pollOptions() {
      return Array.isArray(this.blog.cta_options) ? this.blog.cta_options : [];
    },
    /**
     * Satu kartu per pilihan untuk poll, jadi jumlah kartunya mengikuti announcement dan
     * bukan angka tetap. Tidak diberi warna: pilihan poll tidak punya sisi baik dan buruk
     * seperti Interested / Not interested, dan mewarnainya menyarankan penilaian yang
     * tidak ada.
     */
    summaryCards() {
      if (this.isPoll) {
        const cards = this.pollOptions.map((option) => ({
          key: option.key,
          label: option.label,
          total: this.summary.by_response?.[option.key] || 0,
          class: '',
        }));
        cards.push({
          key: '__total', label: 'Total responded', total: this.summary.total || 0, class: '',
        });
        return cards;
      }
      return [
        {
          key: 'interested', label: 'Interested', total: this.summary.interested || 0, class: 'text-success',
        },
        {
          key: 'not_interested', label: 'Not interested', total: this.summary.not_interested || 0, class: 'text-danger',
        },
        {
          key: '__total', label: 'Total responded', total: this.summary.total || 0, class: '',
        },
      ];
    },
    responseOptions() {
      if (this.isPoll) {
        return this.pollOptions.map((option) => ({ value: option.key, label: option.label }));
      }
      return [
        { value: 'interested', label: 'Interested' },
        { value: 'not_interested', label: 'Not interested' },
      ];
    },
    currentPage: {
      get() {
        return this.search.page;
      },
      set(val) {
        this.search.page = val;
        this.getList();
      },
    },
  },
  mounted() {
    this.getDetail();
    this.getList();
  },
  methods: {
    /**
     * Key yang tidak ada di daftar ditampilkan apa adanya. Seharusnya tidak pernah terjadi —
     * BE menolak menghapus pilihan yang sudah dijawab — tapi key mentah masih lebih berguna
     * daripada sel kosong kalau invariannya jebol lewat jalur lain.
     */
    labelFor(value) {
      if (this.isPoll) {
        return this.pollOptions.find((option) => option.key === value)?.label || value || '-';
      }
      return value === 'interested' ? 'Interested' : 'Not interested';
    },
    badgeClass(value) {
      if (this.isPoll) return 'badge-info';
      return value === 'interested' ? 'badge-success' : 'badge-danger';
    },
    async getDetail() {
      const { id } = this.$route.params;
      await blogNotifApi.getDetail(id).then(async (response) => {
        await popupErrorMessages(response);
        this.blog = response.data || {};
      });
    },
    async getList() {
      this.loading.table = true;
      const { id } = this.$route.params;
      await blogNotifApi.getResponses(id, {
        page: this.search.page,
        per_page: this.search.per_page,
        response: this.search.response || undefined,
      }).then(async (response) => {
        await popupErrorMessages(response);
        const { data } = response;
        this.rows = data.rows || [];
        this.total_row = data.total || 0;
        this.summary = data.summary || {
          interested: 0, not_interested: 0, total: 0, by_response: {},
        };
      });
      this.loading.table = false;
    },
    doSearch() {
      this.search.page = 1;
      this.getList();
    },
    async exportCsv() {
      this.loading.export = true;
      const { id } = this.$route.params;
      await blogNotifApi.exportResponses(id).then(async (response) => {
        await popupErrorMessages(response);
        // BE mengunggah CSV ke R2 dan mengembalikan URL-nya, bukan file-nya langsung.
        if (response.data?.secure_url) {
          window.open(response.data.secure_url, '_blank');
        }
      });
      this.loading.export = false;
    },
  },
};
</script>
